// useFlightSearch — lookup-form state + the explicit on-demand flight search flow.
//
// Extracted verbatim from `FlightLookup.js` (behavior-preserving refactor, issue
// #240). This hook owns everything the FlightLookup form and result panel need:
//
//   State:
//     - lookupForm        ({ airline, flightNumber, date, departureAirport, arrivalAirport })
//     - loading           (request in flight / disabled controls)
//     - flightInfo        (the resolved flight rendered by the result panel, or null)
//     - error             (the error-banner string, or null)
//     - lastAddedFlight   (the just-added flight, drives the "added" banner + pre-fill)
//
//   Effect (deps preserved exactly):
//     - sync form fields from changed `default*` props
//       ([defaultDate, defaultDepartureAirport, defaultArrivalAirport])
//
// IMPORTANT — there is NO debounce/timer here. The 9.1 baseline established that
// FlightLookup performs the lookup on an EXPLICIT button click
// (`handleLookupFlight`); typing only updates local form state. That behavior is
// preserved exactly: no debounce, no auto-search-on-type is introduced.
//
// Service side effects, request URL, call COUNTS, relative ORDER, and the
// toast/error fallbacks are preserved EXACTLY:
//   - handleLookupFlight: normalizeFlightQuery → buildSearchDateRange →
//     toast.loading('Looking up flight information...', { id: 'lookup' }) →
//     authFetch(`${FLIGHT_AWARE_API_BASE}/${encodeURIComponent(flightIdent)}?start=...&end=...`).
//     On `!response.ok` or empty `data.flights` it throws; the catch surfaces
//     toast.error(...) AND sets the error banner. On success it filters by
//     airports (filterFlightsByAirports may THROW the descriptive no-match error,
//     caught by the same try/catch), resolves the match via resolveMatchedFlight
//     (with a bound `FlightAwareService#parseFlightData` injected exactly as the
//     source instantiated it), builds the flightInfo, sets it, then
//     toast.success('Flight found!', { id: 'lookup' }) — in that order. `finally`
//     clears loading.
//   - handleAddFlight: guards `if (!flightInfo || !onFlightFound) return;`, builds
//     the trip flight via buildTripFlight, calls onFlightFound(tripFlight), records
//     it as lastAddedFlight, then resets airline/flightNumber/arrivalAirport and
//     PRE-FILLS departureAirport with the just-added flight's arrivalAirport.
//   - handleLookupChange normalizes the changed field with `.trim().toUpperCase()`
//     and clears the error; handleDateChange sets the raw date and clears the error.
//
// Pure logic is reused from `../logic/` rather than duplicated.
//
// `use`-prefixed, contains no JSX and performs no rendering.
//
// Validates: Requirements 3.2, 3.4, 1.4

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { FLIGHT_AWARE_API_BASE } from '../../../config/environment.js';
import { authFetch } from '../../../utils/authFetch';
import FlightAwareService from '../../../services/flightAwareAPI.js';

import normalizeFlightQuery from '../logic/normalizeFlightQuery.js';
import buildSearchDateRange from '../logic/buildSearchDateRange.js';
import buildDefaultFormDate from '../logic/buildDefaultFormDate.js';
import filterFlightsByAirports from '../logic/filterFlightsByAirports.js';
import resolveMatchedFlight from '../logic/resolveMatchedFlight.js';
import buildFlightInfo from '../logic/buildFlightInfo.js';
import buildTripFlight from '../logic/buildTripFlight.js';

/**
 * Owns the lookup-form state and the explicit (button-click) search flow.
 *
 * @param {Object} params
 * @param {(tripFlight: Object) => void} [params.onFlightFound] - Callback invoked
 *   with the normalized trip flight when the user adds the found flight.
 * @param {() => void} [params.onCancel] - Cancel callback, passed through to the
 *   container for the close button (no behavior here beyond pass-through).
 * @param {string} [params.defaultDate] - Optional initial/synced date (`YYYY-MM-DD`).
 * @param {string} [params.defaultDepartureAirport] - Optional initial/synced From airport.
 * @param {string} [params.defaultArrivalAirport] - Optional initial/synced To airport.
 * @returns {{
 *   lookupForm: { airline: string, flightNumber: string, date: string, departureAirport: string, arrivalAirport: string },
 *   loading: boolean,
 *   flightInfo: (Object|null),
 *   error: (string|null),
 *   lastAddedFlight: (Object|null),
 *   handleLookupChange: (field: string, value: string) => void,
 *   handleDateChange: (value: string) => void,
 *   handleLookupFlight: () => Promise<void>,
 *   handleAddFlight: () => void,
 *   onCancel: (() => void)|undefined
 * }} The flight-search API consumed by the container.
 */
export function useFlightSearch({
  onFlightFound,
  onCancel,
  defaultDate,
  defaultDepartureAirport,
  defaultArrivalAirport,
} = {}) {
  const [loading, setLoading] = useState(false);
  const [lookupForm, setLookupForm] = useState({
    airline: '',
    flightNumber: '',
    date: defaultDate || buildDefaultFormDate(), // Default to today (local timezone)
    departureAirport: defaultDepartureAirport || '',
    arrivalAirport: defaultArrivalAirport || ''
  });

  const [flightInfo, setFlightInfo] = useState(null);
  const [error, setError] = useState(null);
  const [lastAddedFlight, setLastAddedFlight] = useState(null);

  // Update form when defaults change
  useEffect(() => {
    if (defaultDate) {
      setLookupForm(prev => ({ ...prev, date: defaultDate }));
    }
    if (defaultDepartureAirport) {
      setLookupForm(prev => ({ ...prev, departureAirport: defaultDepartureAirport }));
    }
    if (defaultArrivalAirport) {
      setLookupForm(prev => ({ ...prev, arrivalAirport: defaultArrivalAirport }));
    }
  }, [defaultDate, defaultDepartureAirport, defaultArrivalAirport]);

  const handleLookupChange = (field, value) => {
    setLookupForm(prev => ({
      ...prev,
      [field]: value.trim().toUpperCase()
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleDateChange = (value) => {
    setLookupForm(prev => ({
      ...prev,
      date: value
    }));
    if (error) setError(null);
  };

  // Look up flight information from FlightAware
  const handleLookupFlight = async () => {
    setLoading(true);
    setFlightInfo(null);
    setError(null);

    try {
      // Sanitize and trim airline and flight number inputs to remove any special characters
      const { airline, flightNumber, flightIdent } = normalizeFlightQuery(
        lookupForm.airline,
        lookupForm.flightNumber
      );

      const { startDate, endDate } = buildSearchDateRange(lookupForm.date);

      toast.loading('Looking up flight information...', { id: 'lookup' });

      const response = await authFetch(
        `${FLIGHT_AWARE_API_BASE}/${encodeURIComponent(flightIdent)}?start=${startDate}&end=${endDate}`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch flight information: ${response.status}`);
      }

      const data = await response.json();

      if (!data.flights || data.flights.length === 0) {
        throw new Error('No flights found for this flight number and date');
      }

      // Filter by airports
      var _wantOrigin = (lookupForm.departureAirport || '').trim().toUpperCase();
      var _wantDest = (lookupForm.arrivalAirport || '').trim().toUpperCase();
      const filteredData = filterFlightsByAirports(data, _wantOrigin, _wantDest, airline, flightNumber);

      // Create target flight context for matching
      const targetFlight = {
        departureAirport: null, // already filtered above
        arrivalAirport: null,
        flightId: flightIdent,
        departureTime: startDate
      };

      // FlightAwareService instantiated exactly as the source did (once per
      // lookup); its bound parseFlightData is injected into the pure matcher.
      const flightAwareService = new FlightAwareService();
      const flight = resolveMatchedFlight(
        filteredData,
        data,
        targetFlight,
        flightAwareService.parseFlightData.bind(flightAwareService)
      );

      if (!flight) {
        throw new Error('No flights found for this flight number and date');
      }

      setFlightInfo(buildFlightInfo(flight, airline, flightNumber));
      toast.success('Flight found!', { id: 'lookup' });

    } catch (error) {
      toast.error(error.message || 'Failed to look up flight', { id: 'lookup' });
      setError(error.message || 'Failed to look up flight information');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFlight = () => {
    if (!flightInfo || !onFlightFound) return;

    // Convert flight info to trip flight format
    const tripFlight = buildTripFlight(flightInfo);

    onFlightFound(tripFlight);
    setLastAddedFlight(tripFlight);

    // Reset form, pre-fill departure airport with this flight's arrival for easy connection entry
    setFlightInfo(null);
    setLookupForm(prev => ({
      ...prev,
      airline: '',
      flightNumber: '',
      departureAirport: tripFlight.arrivalAirport || '',
      arrivalAirport: ''
    }));
  };

  return {
    lookupForm,
    loading,
    flightInfo,
    error,
    lastAddedFlight,
    handleLookupChange,
    handleDateChange,
    handleLookupFlight,
    handleAddFlight,
    onCancel,
  };
}

export default useFlightSearch;
